import { useEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from './utils/LanguageContext';
import AdminDashboard from './pages/AdminDashboard';
import Home from './pages/Home';
import SourcingFournisseurs from './pages/SourcingFournisseurs';
import Approvisionnement from './pages/Approvisionnement';
import GestionTransport from './pages/GestionTransport';
import FoireCanton from './pages/FoireCanton';
import CGU from './pages/CGU';
import ProductList from './pages/ProductList';
import ProductDetail from './pages/ProductDetail';
import UserAccount from './pages/UserAccount';
import Cart from './pages/Cart';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { getToken, getUserRole } from './utils/auth';
import './App.css';
import footerLogo from './assets/logo5.png';
import brandLogo from './assets/logo5.png';
import transportImg from './assets/transport.jpg';
import usineImg from './assets/imgusine.jpg';
import mtaImg from './assets/mta.jpg';

const LANG_LABELS = { fr: 'FR', en: 'EN', zh: '中文' };
const LANG_OPTIONS = [
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'zh', flag: '🇨🇳', label: '中文' },
];

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLanguage, t } = useLanguage();
  const headerRef = useRef(null);
  const [authState, setAuthState] = useState(() => ({
    token: getToken(),
    role: getUserRole()
  }));
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [transportOpen, setTransportOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [servicesMobileOpen, setServicesMobileOpen] = useState(false);
  const [navBottom, setNavBottom] = useState(100);
  const [cantonFairVisible, setCantonFairVisible] = useState(false);

  const SERVICES_ITEMS = [
    {
      title: t('nav.megamenu.sourcing.title'),
      img: usineImg,
      text: t('nav.megamenu.sourcing.text'),
      href: '/sourcing-fournisseurs',
    },
    {
      title: t('nav.megamenu.appro.title'),
      img: transportImg,
      text: t('nav.megamenu.appro.text'),
      href: '/approvisionnement',
    },
  ];

  useEffect(() => {
    setAuthState({ token: getToken(), role: getUserRole() });
    setServicesOpen(false);
    setTransportOpen(false);
    setLangOpen(false);
    fetch('http://localhost:5001/api/content/canton-fair')
      .then(r => r.json())
      .then(d => setCantonFairVisible(!!d.visible))
      .catch(() => {});
  }, [location]);

  useEffect(() => {
    const handler = (e) => setCantonFairVisible(!!e.detail.visible);
    window.addEventListener('cantonFairChanged', handler);
    return () => window.removeEventListener('cantonFairChanged', handler);
  }, []);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'token') setAuthState({ token: getToken(), role: getUserRole() });
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (transportOpen && headerRef.current) {
      const topbar = headerRef.current.querySelector('.navigation__topbar');
      const main = headerRef.current.querySelector('.navigation__main');
      setNavBottom((topbar?.offsetHeight || 0) + (main?.offsetHeight || 0));
    }
  }, [transportOpen]);

  useEffect(() => {
    if (!servicesOpen && !transportOpen && !langOpen) return;
    const onOutside = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setServicesOpen(false);
        setTransportOpen(false);
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [servicesOpen, transportOpen, langOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    setServicesOpen(false);
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  const handleScrollTo = (sectionId) => {
    setMenuOpen(false);
    setServicesOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  const linkClass = ({ isActive }) =>
    `navigation__link${isActive ? ' navigation__link--active' : ''}`;

  const closeMenu = () => { setMenuOpen(false); setServicesOpen(false); setLangOpen(false); setServicesMobileOpen(false); };

  return (
    <header ref={headerRef} className="navigation">
      {/* ── Main bar ── */}
      <div className="navigation__main">
        <NavLink to="/" className="navigation__brand" onClick={closeMenu}>
          <img src={brandLogo} alt="GSSG logo" className="navigation__brand-logo" />
          <div className="navigation__brand-text">
            <span className="navigation__brand-title">GSSG</span>
            <small className="navigation__brand-subtitle" translate="no">Global Sourcing Solution Group</small>
          </div>
        </NavLink>

        {/* Desktop links */}
        <nav className="navigation__links" aria-label="Navigation principale">
          <button
            type="button"
            className={`navigation__link navigation__link--dropdown${servicesOpen ? ' navigation__link--active' : ''}`}
            onClick={() => { setServicesOpen(o => !o); setTransportOpen(false); setLangOpen(false); }}
            aria-expanded={servicesOpen}
          >
            {t('nav.services')}
            <svg className={`nav-chevron${servicesOpen ? ' nav-chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <button
            type="button"
            className={`navigation__link navigation__link--dropdown${transportOpen ? ' navigation__link--active' : ''}`}
            onClick={() => { setTransportOpen(o => !o); setServicesOpen(false); setLangOpen(false); }}
            aria-expanded={transportOpen}
          >
            {t('nav.transport')}
            <svg className={`nav-chevron${transportOpen ? ' nav-chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {cantonFairVisible && (
            <NavLink to="/foire-canton" className={linkClass} onClick={closeMenu}>
              {t('nav.didyouknow')}
            </NavLink>
          )}
          <button type="button" className="navigation__link" onClick={() => handleScrollTo('about-section')}>
            {t('nav.about')}
          </button>
          {authState.token && authState.role === 'admin' && (
            <NavLink to="/admin" className={`${linkClass({ isActive: location.pathname.startsWith('/admin') })} navigation__link--admin`} onClick={closeMenu}>
              {t('nav.admin')}
            </NavLink>
          )}
        </nav>

        {/* Language switcher */}
        <div className="nav-lang">
          <button
            type="button"
            className={`nav-lang__btn${langOpen ? ' nav-lang__btn--open' : ''}`}
            onClick={() => { setLangOpen(o => !o); setServicesOpen(false); setTransportOpen(false); }}
            aria-label="Changer de langue"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" width="15" height="15"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            <span>{LANG_LABELS[lang]}</span>
            <svg className={`nav-chevron${langOpen ? ' nav-chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {langOpen && (
            <ul className="nav-lang__dropdown" role="listbox">
              {LANG_OPTIONS.map(({ code, flag, label }) => (
                <li key={code}>
                  <button
                    type="button"
                    className={`nav-lang__option${lang === code ? ' nav-lang__option--active' : ''}`}
                    onClick={() => { setLanguage(code); setLangOpen(false); }}
                    role="option"
                    aria-selected={lang === code}
                  >
                    <span>{flag}</span>
                    <span>{label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <button type="button" className="navigation__cta" onClick={() => handleScrollTo('contact-section')}>
          {t('nav.contact')}
        </button>

        {authState.role === 'admin' && (
          <div className="navigation__session">
            {authState.token ? (
              <>
                <NavLink to="/account" className="navigation__session-button navigation__session-button--ghost" onClick={closeMenu}>
                  {t('nav.account')}
                </NavLink>
                <button type="button" onClick={handleLogout} className="navigation__session-button navigation__session-button--logout">
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <NavLink to="/login" className="navigation__session-button navigation__session-button--primary" onClick={closeMenu}>
                {t('nav.clientarea')}
              </NavLink>
            )}
          </div>
        )}

        <button
          type="button"
          className={`navigation__toggle${menuOpen ? ' navigation__toggle--open' : ''}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Menu"
        >
          <span /><span /><span />
        </button>
      </div>

      {/* ── Services mega-menu (desktop) ── */}
      <div className={`nav-megamenu${servicesOpen ? ' nav-megamenu--open' : ''}`} aria-hidden={!servicesOpen}>
        <div className="nav-megamenu__inner">
          {SERVICES_ITEMS.map((item) => {
            const cardContent = (
              <>
                <div className="nav-megamenu__overlay" />
                <div className="nav-megamenu__content">
                  <h3 className="nav-megamenu__title" translate="no">{item.title.toUpperCase()}</h3>
                  <p className="nav-megamenu__text">{item.text}</p>
                </div>
              </>
            );
            return item.href ? (
              <Link
                key={item.title}
                to={item.href}
                className="nav-megamenu__card"
                style={{ backgroundImage: `url(${item.img})`, textDecoration: 'none' }}
                onClick={() => setServicesOpen(false)}
              >
                {cardContent}
              </Link>
            ) : (
              <div
                key={item.title}
                className="nav-megamenu__card"
                style={{ backgroundImage: `url(${item.img})` }}
              >
                {cardContent}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Transport mega-menu (desktop) ── */}
      <div
        className={`nav-megamenu--transport${transportOpen ? ' nav-megamenu--transport--open' : ''}`}
        style={{ top: navBottom }}
        aria-hidden={!transportOpen}
      >
        <div className="nav-transport-wrap">
          <Link
            to="/gestion-transport"
            className="nav-transport-card"
            style={{ backgroundImage: `url(${mtaImg})`, textDecoration: 'none' }}
            onClick={() => setTransportOpen(false)}
          >
            <div className="nav-transport-card__overlay" />
            <div className="nav-transport-card__content">
              <h3 className="nav-transport-card__title">{t('nav.transport.card.title')}</h3>
              <p className="nav-transport-card__text">{t('nav.transport.card.text')}</p>
            </div>
          </Link>
        </div>
      </div>

      {/* ── Mobile menu ── */}
      <nav className={`navigation__menu${menuOpen ? ' navigation__menu--open' : ''}`} aria-label="Menu mobile">
        <div className="nav-mobile-group">
          <button
            type="button"
            className={`navigation__link navigation__link--dropdown${servicesMobileOpen ? ' navigation__link--active' : ''}`}
            onClick={() => setServicesMobileOpen(o => !o)}
          >
            {t('nav.services')}
            <svg className={`nav-chevron${servicesMobileOpen ? ' nav-chevron--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div className={`nav-mobile-submenu${servicesMobileOpen ? ' nav-mobile-submenu--open' : ''}`}>
            <Link to="/sourcing-fournisseurs" className="nav-mobile-submenu__link" onClick={closeMenu}>
              {t('nav.megamenu.sourcing.title')}
            </Link>
            <Link to="/approvisionnement" className="nav-mobile-submenu__link" onClick={closeMenu}>
              {t('nav.megamenu.appro.title')}
            </Link>
          </div>
        </div>
        <button type="button" className="navigation__link" onClick={() => { closeMenu(); navigate('/gestion-transport'); }}>{t('nav.transport')}</button>
        {cantonFairVisible && (
          <NavLink to="/foire-canton" className={linkClass} onClick={closeMenu}>{t('nav.didyouknow')}</NavLink>
        )}
        <button type="button" className="navigation__link" onClick={() => handleScrollTo('about-section')}>{t('nav.about')}</button>
        {authState.token && (
          <NavLink to="/account" className={linkClass} onClick={closeMenu}>{t('nav.account')}</NavLink>
        )}
        {authState.token && authState.role === 'admin' && (
          <NavLink to="/admin" className={linkClass} onClick={closeMenu}>{t('nav.admin')}</NavLink>
        )}
        {/* Mobile language switcher */}
        <div className="nav-lang-mobile">
          {LANG_OPTIONS.map(({ code, flag, label }) => (
            <button
              key={code}
              type="button"
              className={`nav-lang-mobile__btn${lang === code ? ' nav-lang-mobile__btn--active' : ''}`}
              onClick={() => { setLanguage(code); closeMenu(); }}
            >
              {flag} {label}
            </button>
          ))}
        </div>
        <div className="navigation__menu-session">
          {authState.token ? (
            <button type="button" onClick={handleLogout} className="navigation__session-button navigation__session-button--logout">
              {t('nav.logout')}
            </button>
          ) : (
            <>
              <NavLink to="/login" className="navigation__session-button navigation__session-button--primary" onClick={closeMenu}>{t('nav.signin')}</NavLink>
              <NavLink to="/register" className="navigation__session-button navigation__session-button--ghost" onClick={closeMenu}>{t('nav.signup')}</NavLink>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}

function Footer() {
  const { t } = useLanguage();
  return (
    <footer className="app-footer">
      <div className="app-footer__inner">

        {/* ── Col 1 : Marque + Contact ── */}
        <div className="app-footer__column app-footer__column--brand">
          <img src={footerLogo} alt="Logo GSSG" className="app-footer__logo" />
          <p className="app-footer__tagline" translate="no">Global Sourcing Solution Group</p>
          <p className="app-footer__desc">{t('footer.desc')}</p>
          <ul className="app-footer__contact-list">
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 3.09 4.18 2 2 0 0 1 5.07 2h3a2 2 0 0 1 2 1.72c.13 1 .37 1.97.72 2.9a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 5.99 6l1.18-1.18a2 2 0 0 1 2.11-.45c.93.35 1.9.59 2.9.72A2 2 0 0 1 22 16.92z"/></svg>
              <a href="tel:+33755201386">+33 7 55 20 13 86</a>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              <a href="mailto:noreply@globalsourcingsolutiongroup.com">noreply@globalsourcingsolutiongroup.com</a>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <a href="https://www.google.com/maps/search/?api=1&query=52+avenue+de+la+R%C3%A9publique,+78500+Sartrouville" target="_blank" rel="noopener noreferrer">
                52 avenue de la République, 78500 Sartrouville
              </a>
            </li>
          </ul>
        </div>

        {/* ── Col 2 : Liens ── */}
        <div className="app-footer__column">
          <h4>{t('footer.nav')}</h4>
          <ul className="app-footer__list">
            <li><Link to="/" state={{ scrollTo: 'about-section' }}>{t('footer.link.about')}</Link></li>
            <li><Link to="/" state={{ scrollTo: 'services-section' }}>{t('footer.link.services')}</Link></li>
            <li><Link to="/sourcing-fournisseurs">{t('footer.link.sourcing')}</Link></li>
            <li><Link to="/approvisionnement">{t('footer.link.appro')}</Link></li>
            <li><Link to="/gestion-transport">{t('footer.link.transport')}</Link></li>
          </ul>
          <h4 style={{ marginTop: '1.4rem' }}>{t('footer.info')}</h4>
          <ul className="app-footer__list">
            <li><Link to="/cgu">CGU</Link></li>
            <li><Link to="/login">{t('nav.clientarea')}</Link></li>
          </ul>
        </div>

        {/* ── Col 3 : Réseaux sociaux ── */}
        <div className="app-footer__column app-footer__column--social">
          <h4>{t('footer.follow')}</h4>
          <p className="app-footer__social-desc">{t('footer.social_desc')}</p>
          <div className="app-footer__social">
            <a href="https://www.facebook.com/?locale=fr_FR" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="app-footer__social-link">
              <svg viewBox="0 0 24 24" className="app-footer__social-icon" aria-hidden="true"><path d="M22 12.07C22 6.48 17.52 2 11.93 2 6.35 2 2 6.48 2 12.07c0 4.99 3.65 9.13 8.42 9.93v-7.03H7.9v-2.9h2.52V9.64c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.27c-1.25 0-1.64.79-1.64 1.6v1.9h2.79l-.45 2.9h-2.34V22c4.77-.8 8.42-4.94 8.42-9.93z"/></svg>
              <span>Facebook</span>
            </a>
            <a href="https://x.com/?lang=fr" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="app-footer__social-link">
              <svg viewBox="0 0 24 24" className="app-footer__social-icon" aria-hidden="true"><path d="M4 3h3.8l4.2 5.6L16.8 3H21l-7 8.4 7 9.6h-3.8l-4.6-6.2L7 21H3l7.6-9.1z"/></svg>
              <span>X (Twitter)</span>
            </a>
            <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="app-footer__social-link">
              <svg viewBox="0 0 24 24" className="app-footer__social-icon" aria-hidden="true"><path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 7.5zm0 2A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 9.5zm4.75-3.5a1 1 0 1 1-1 1 1 1 0 0 1 1-1z"/></svg>
              <span>Instagram</span>
            </a>
            <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="app-footer__social-link">
              <svg viewBox="0 0 24 24" className="app-footer__social-icon" aria-hidden="true"><path d="M4.98 3.5a2 2 0 1 1-.02 4 2 2 0 0 1 .02-4zM3 8.79h3.96V21H3zm6.74 0H14v1.67h.06c.56-1.06 1.92-2.17 3.95-2.17 4.23 0 5.01 2.57 5.01 5.91V21h-3.96v-6.05c0-1.44-.03-3.29-2.01-3.29-2.02 0-2.33 1.58-2.33 3.19V21H9.74z"/></svg>
              <span>LinkedIn</span>
            </a>
          </div>
        </div>

      </div>

      <div className="app-footer__bottom">
        <div className="app-footer__legal">© {new Date().getFullYear()} <span translate="no">Global Sourcing Solution Group</span>. {t('footer.rights')}</div>
        <button type="button" className="app-footer__backtotop" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          {t('footer.backtotop')}
        </button>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="app-shell">
      <Navigation />
      <main className="app-content">
        <Routes>
          <Route path="/admin/*" element={<ProtectedRoute><AdminRoute><AdminDashboard /></AdminRoute></ProtectedRoute>} />
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<ProductList />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/account" element={<ProtectedRoute><UserAccount /></ProtectedRoute>} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/sourcing-fournisseurs" element={<SourcingFournisseurs />} />
          <Route path="/approvisionnement" element={<Approvisionnement />} />
          <Route path="/gestion-transport" element={<GestionTransport />} />
          <Route path="/foire-canton" element={<FoireCanton />} />
          <Route path="/cgu" element={<CGU />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}

export default App;
